import { useMemo, useState } from "react";

import { NotificationExpandToggle } from "./NotificationExpandToggle";
import { Column, Row, Text } from "@/components";
import { DashboardTaskOverdueBadge } from "@/components/dashboard/seven-day-schedule/DashboardTaskOverdueBadge";

import { DASHBOARD_SCHEDULE_DAYS, MS_PER_DAY } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_NOTIFICATION_SURFACES, MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { cn } from "@/lib/utils";
import { NotificationPlacementCheckInsPayload, NotificationType } from "@/types";

export const NotificationPlacementCheckInsList = ({
  isCompact = false,
  payload,
  trackingSurface,
}: {
  isCompact?: boolean;
  payload: NotificationPlacementCheckInsPayload;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { checkIns } = payload;

  // Fixed Today→+6 skeleton so every day shows (empty ones get a placeholder);
  // overdue check-ins fold into Today.
  const groups = useMemo(() => {
    const today = dates.getStartOfLocalDay(new Date());
    const buckets = Array.from({ length: DASHBOARD_SCHEDULE_DAYS }, (_, i) => {
      const day = new Date(today);
      day.setDate(today.getDate() + i);
      return {
        label: dates.relativeDayLabel(day),
        items: [] as typeof checkIns,
      };
    });
    for (const checkIn of checkIns) {
      const dueDay = dates.getStartOfLocalDay(
        dates.taskDueDate(checkIn.dueDate),
      );
      const diff = Math.round(
        (dueDay.getTime() - today.getTime()) / MS_PER_DAY,
      );
      const index = Math.min(Math.max(diff, 0), DASHBOARD_SCHEDULE_DAYS - 1);
      buckets[index].items.push(checkIn);
    }
    return buckets;
  }, [checkIns]);

  const isOverdue = (dueDate: string): boolean =>
    dates.getStartOfLocalDay(dates.taskDueDate(dueDate)).getTime() <
    dates.getStartOfLocalDay(new Date()).getTime();

  const render = () => (
    <Column className="gap-0.5">
      <NotificationExpandToggle
        isExpanded={isExpanded}
        notificationType={NotificationType.PLACEMENT_CHECKINS}
        setIsExpanded={setIsExpanded}
        trackingSurface={trackingSurface}
      >
        {checkIns.length} {checkIns.length === 1 ? "check-in" : "check-ins"}
      </NotificationExpandToggle>
      {isExpanded ? (
        <Column className="gap-2 border-l border-slate-200 pl-3 pt-1">
          {groups.map((group) => (
            <Column key={group.label} className="gap-0.5">
              <Text className="text-xs font-semibold text-slate-400">
                {group.label}
              </Text>
              {group.items.length === 0 ? (
                <Text className="px-2 py-1 text-xs font-normal text-slate-300">
                  Nothing due
                </Text>
              ) : (
                group.items.map((checkIn) => (
                  <Column
                    key={checkIn.candidacyId}
                    className="cursor-pointer gap-0 rounded-md px-2 py-1 hover:bg-slate-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      mixpanelUtils.trackRowClick(
                        "Notification list item",
                        MIXPANEL_SCREENS.NOTIFICATIONS,
                        {
                          notification_type:
                            NotificationType.PLACEMENT_CHECKINS,
                          surface: trackingSurface,
                        },
                      );
                      window.open(getCandidacyLink(checkIn), "_blank");
                    }}
                  >
                    <Row className="items-center gap-1.5">
                      <Text className={cn("text-sm", isCompact && "text-xs")}>
                        {checkIn.candidateName}
                      </Text>
                      {isOverdue(checkIn.dueDate) ? (
                        <DashboardTaskOverdueBadge
                          dueDate={checkIn.dueDate}
                          compact
                        />
                      ) : null}
                    </Row>
                    <Text className="text-xs font-normal text-slate-500">
                      {checkIn.milestone}-day at {checkIn.customerName}
                    </Text>
                  </Column>
                ))
              )}
            </Column>
          ))}
        </Column>
      ) : null}
    </Column>
  );

  return render();
};
